import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
  useTheme,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";

export interface PropertyFilterValues {
  minBeds: string;
  minBaths: string;
  minYearBuilt: number;
  maxYearBuilt: number;
  sortBy: string;
  // Amenity filters
  hasAttic: boolean;
  hasBasement: boolean;
  hasDeck: boolean;
  hasBalcony: boolean;
  hasGarage: boolean;
  hasPatio: boolean;
  hasPool: boolean;
  hasAirConditioning: boolean;
}

interface PropertyFiltersProps {
  filters: PropertyFilterValues;
  onFilterChange: (filters: PropertyFilterValues) => void;
  totalCount: number;
}

const PropertyFilters: React.FC<PropertyFiltersProps> = ({
  filters,
  onFilterChange,
  totalCount,
}) => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<PropertyFilterValues>(filters);

  // Sync localFilters with incoming filters prop
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Bedroom options
  const bedroomOptions = [
    { value: "0", label: "Any" },
    { value: "1", label: "1+" },
    { value: "2", label: "2+" },
    { value: "3", label: "3+" },
    { value: "4", label: "4+" },
    { value: "5", label: "5+" },
  ];

  // Bathroom options
  const bathroomOptions = [
    { value: "0", label: "Any" },
    { value: "1", label: "1+" },
    { value: "2", label: "2+" },
    { value: "3", label: "3+" },
    { value: "4", label: "4+" },
  ];

  // Sort options
  const sortOptions = [
    { value: "relevance", label: "Most Relevant" },
    { value: "price_asc", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "year_built_desc", label: "Newest Homes" },
    { value: "year_built_asc", label: "Oldest Homes" },
    { value: "building_size_desc", label: "Largest Homes" },
    { value: "building_size_asc", label: "Smallest Homes" },
    { value: "lot_size_desc", label: "Largest Lots" },
    { value: "last_sale_date_desc", label: "Recently Sold" },
  ];

  // Amenity filter options
  const amenityOptions: { field: keyof PropertyFilterValues; label: string }[] = [
    { field: "hasPool", label: "Pool" },
    { field: "hasGarage", label: "Garage" },
    { field: "hasBasement", label: "Basement" },
    { field: "hasAttic", label: "Attic" },
    { field: "hasDeck", label: "Deck" },
    { field: "hasPatio", label: "Patio" },
    { field: "hasBalcony", label: "Balcony" },
    { field: "hasAirConditioning", label: "Air Conditioning" },
  ];

  const handleLocalFilterChange = (field: keyof PropertyFilterValues, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    setDrawerOpen(false);
  };

  const handleResetFilters = () => {
    const resetFilters: PropertyFilterValues = {
      minBeds: "0",
      minBaths: "0",
      minYearBuilt: 1800,
      maxYearBuilt: new Date().getFullYear(),
      sortBy: "relevance",
      hasAttic: false,
      hasBasement: false,
      hasDeck: false,
      hasBalcony: false,
      hasGarage: false,
      hasPatio: false,
      hasPool: false,
      hasAirConditioning: false,
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
    setDrawerOpen(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.minBeds !== "0") count++;
    if (localFilters.minBaths !== "0") count++;
    if (localFilters.minYearBuilt > 1800) count++;
    if (localFilters.maxYearBuilt < new Date().getFullYear()) count++;
    // Count amenity filters
    if (localFilters.hasAttic) count++;
    if (localFilters.hasBasement) count++;
    if (localFilters.hasDeck) count++;
    if (localFilters.hasBalcony) count++;
    if (localFilters.hasGarage) count++;
    if (localFilters.hasPatio) count++;
    if (localFilters.hasPool) count++;
    if (localFilters.hasAirConditioning) count++;
    return count;
  };

  const FilterContent = () => (
    <Box sx={{ width: "100%", height: "100%" }} padding={2}>
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Filters
        </Typography>
        <IconButton onClick={() => setDrawerOpen(false)} size="small" aria-label="Close filters">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Filter Content */}
      <Box
        sx={{
          p: 2,
          overflowY: "auto",
          height: "calc(100% - 140px)",
        }}
      >
        <Stack spacing={3}>
          {/* Basic Filters */}
          <Box>
            <Grid2 container spacing={2}>
              {/* Bedrooms */}
              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="beds-label">Beds</InputLabel>
                  <Select
                    labelId="beds-label"
                    id="beds-select"
                    name="minBeds"
                    value={localFilters.minBeds}
                    label="Beds"
                    onChange={(e) =>
                      handleLocalFilterChange("minBeds", e.target.value)
                    }
                  >
                    {bedroomOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

              {/* Bathrooms */}
              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="baths-label">Baths</InputLabel>
                  <Select
                    labelId="baths-label"
                    id="baths-select"
                    name="minBaths"
                    value={localFilters.minBaths}
                    label="Baths"
                    onChange={(e) =>
                      handleLocalFilterChange("minBaths", e.target.value)
                    }
                  >
                    {bathroomOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
            </Grid2>
          </Box>

          {/* Amenities */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              Amenities
            </Typography>
            <Grid2 container spacing={0}>
              {amenityOptions.map((amenity) => (
                <Grid2 key={amenity.field} size={{ xs: 6, sm: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localFilters[amenity.field] as boolean}
                        onChange={(e) =>
                          handleLocalFilterChange(amenity.field, e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">{amenity.label}</Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Year Built */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Year Built
            </Typography>
            <Typography variant="caption" color="text.secondary" mb={1.5} display="block">
              {localFilters.minYearBuilt} - {localFilters.maxYearBuilt}
            </Typography>
            <Box sx={{ px: 2, pt: 1 }}>
              <Slider
                getAriaLabel={() => "Year Built"}
                value={[localFilters.minYearBuilt, localFilters.maxYearBuilt]}
                onChange={(_, newValue) => {
                  const [min, max] = newValue as number[];
                  handleLocalFilterChange("minYearBuilt", min);
                  handleLocalFilterChange("maxYearBuilt", max);
                }}
                valueLabelDisplay="auto"
                min={1800}
                max={new Date().getFullYear()}
                step={5}
                marks={[
                  { value: 1800, label: "1800" },
                  { value: 1950, label: "1950" },
                  { value: 2000, label: "2000" },
                  { value: new Date().getFullYear(), label: "New" },
                ]}
                sx={{ mb: 1 }}
              />
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Apply/Reset Buttons */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleResetFilters}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleApplyFilters}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Top Bar with Sort and Filter */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 2,
          gap: 2,
        }}
      >
        {/* Sort By */}
        <FormControl size="small" sx={{ width: { xs: "auto", sm: "150px" }, minWidth: { xs: "120px", sm: "150px" }, maxWidth: "150px" }}>
          <InputLabel id="sort-by-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-label"
            id="sort-by-select"
            name="sortBy"
            value={filters.sortBy}
            label="Sort By"
            onChange={(e) => {
              const newFilters = { ...filters, sortBy: e.target.value };
              onFilterChange(newFilters);
            }}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filter Button */}
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={() => setDrawerOpen(true)}
          sx={{ whiteSpace: "nowrap" }}
        >
          Filters
          {getActiveFilterCount() > 0 && (
            <Chip
              label={getActiveFilterCount()}
              size="small"
              color="primary"
              sx={{ ml: 1, height: 20, minWidth: 20 }}
            />
          )}
        </Button>
      </Box>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" mb={2}>
        {totalCount.toLocaleString()} properties found
      </Typography>

      {/* Drawer - Universal for all screen sizes */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            height: { xs: "90vh", sm: "85vh", md: "80vh" },
            maxWidth: { xs: "100%", lg: "1200px" },
            mx: "auto",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <FilterContent />
      </Drawer>
    </>
  );
};

export default PropertyFilters;
